import { useMemo, useState, type FC } from 'react';

import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import CompactNumberWithTooltip from '@/components/CompactNumberWithTooltip';
import FormattedNumber from '@/components/FormattedNumber';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { useActiveAccount } from '@/providers/ActiveAccountProvider';
import { type LeaderboardData, type Campaign, CampaignType } from '@/types';
import { formatAddress, getCompactNumberParts } from '@/utils';
import dayjs from '@/utils/dayjs';

import LeaderboardList from './List';
import MyEntryLabel from './MyEntryLabel';
import LeaderboardOverlay from './Overlay';

const ViewAllButton = ({ onClick }: { onClick: () => void }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'end',
      height: { xs: 'auto', md: '180px' },
      px: { xs: 2, md: 4 },
      pb: 1.5,
      pt: { xs: 1.5, md: 0 },
      position: { xs: 'relative', md: 'absolute' },
      bottom: 0,
      left: 0,
      right: 0,
      background: {
        xs: 'background.paper',
        md: 'linear-gradient(0deg, #251D47 12.72%, rgba(37, 29, 71, 0.00) 100%)',
      },
    }}
  >
    <Button
      variant="outlined"
      fullWidth
      onClick={onClick}
      sx={{
        color: 'neutral.100',
        borderColor: 'accent.main',
        height: 'fit-content',
      }}
    >
      View All
    </Button>
  </Box>
);

const calculateListSlice = (
  leaderboard: { address: string }[],
  activeAddress?: string
): [number, number] => {
  const DEFAULT_START = 3;
  const WINDOW = 5;
  const total = leaderboard.length;

  const userIndex = activeAddress
    ? leaderboard.findIndex((entry) => entry.address === activeAddress)
    : -1;

  if (userIndex === -1) {
    return [DEFAULT_START, DEFAULT_START + WINDOW];
  }

  const idealStart = userIndex - 2;
  const minStart = DEFAULT_START;
  const maxStart = Math.max(DEFAULT_START, total - WINDOW);

  const start = Math.min(maxStart, Math.max(minStart, idealStart));
  const end = start + WINDOW;

  return [start, end];
};

export const formatActualOnDate = (date: string) => {
  const value = dayjs(date);
  const localTime = value.format('HH:mm');

  if (value.isSame(dayjs(), 'day')) {
    return localTime;
  }

  return `${value.format('Do MMM')} ${localTime}`;
};

const getTargetLabel = (campaignType: CampaignType): string => {
  switch (campaignType) {
    case CampaignType.MARKET_MAKING:
    case CampaignType.COMPETITIVE_MARKET_MAKING:
    case CampaignType.THRESHOLD_MARKET_MAKING:
      return 'Volume';
    case CampaignType.HOLDING:
      return 'Balance';
    default:
      return 'Unknown Target';
  }
};

type Props = {
  campaign: Campaign;
  leaderboard: LeaderboardData;
};

const Leaderboard: FC<Props> = ({ campaign, leaderboard }) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const { activeAddress } = useActiveAccount();
  const isMobile = useIsMobile();

  const [listStart, listEnd] = useMemo(
    () => calculateListSlice(leaderboard.data, activeAddress),
    [leaderboard.data, activeAddress]
  );

  const showList = leaderboard.data.length > 3;
  const showViewAllButton = leaderboard.data.length > 8;

  return (
    <Stack
      component="section"
      sx={{
        py: 3,
        mx: { xs: -2, md: 0 },
        px: { xs: 2, md: 0 },
        gap: { xs: 1, md: 3 },
        borderBottom: '1px solid',
        borderColor: 'border.strong',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography
          component="h6"
          sx={{
            color: isMobile ? 'neutral.100' : 'text.primary',
            fontSize: { xs: '20px', md: '16px' },
            fontWeight: { xs: 500, md: 600 },
            letterSpacing: { xs: 0, md: 3.2 },
            textTransform: { xs: 'none', md: 'uppercase' },
          }}
        >
          Leaderboard
        </Typography>
        <Typography
          component="p"
          variant={isMobile ? 'subtitle4' : 'body3'}
          sx={{ opacity: 0.6 }}
        >
          Actual on: {formatActualOnDate(leaderboard.updated_at)}
        </Typography>
      </Box>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          bgcolor: { xs: 'transparent', md: 'background.paper' },
          borderRadius: '8px',
          border: { xs: 'none', md: '1px solid' },
          borderColor: { xs: 'unset', md: 'border.strong' },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'end',
            pt: { xs: 0, md: 4 },
            px: { xs: 0, md: 4 },
            gap: { xs: 0, md: 2 },
            borderBottom: showList ? '1px solid' : 'none',
            borderColor: 'border.strong',
          }}
        >
          {leaderboard.data.slice(0, 3).map((entry) => {
            const { rank, address, result, score, estimated_reward_gross } =
              entry;
            const {
              value: rewardValue,
              suffix: rewardSuffix,
              decimals: rewardDecimals,
            } = getCompactNumberParts(estimated_reward_gross);
            const isMyEntry = address === activeAddress;
            return (
              <Stack
                key={address}
                sx={{
                  alignItems: 'center',
                  px: { xs: 1, md: 4 },
                  pb: { xs: 1.5, md: 3 },
                  flex: 1,
                  maxWidth: 'calc(100% / 3)',
                  height: {
                    xs: rank === 1 ? '192px' : rank === 2 ? '173px' : '155px',
                    md: rank === 1 ? '287px' : rank === 2 ? '251px' : '214px',
                  },
                  borderRadius: '15px 15px 0 0',
                  border: {
                    xs: 'none',
                    md: '1px solid',
                  },
                  borderBottom: { xs: 'none', md: 'none' },
                  borderColor: { xs: 'unset', md: 'border.strong' },
                  background:
                    'linear-gradient(180deg, #3a2e6f 0%, #231d3e 100%)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 0, md: 1 },
                    my: 'auto',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'transparent',
                      fontSize: {
                        xs: rank === 1 ? '42px' : '36px',
                        md: rank === 1 ? '68px' : rank === 2 ? '48px' : '40px',
                      },
                      lineHeight: 1,
                      fontWeight: 700,
                      background:
                        rank === 1
                          ? 'linear-gradient(135deg, #eb5088 46.1%, #5630ff 113.92%)'
                          : 'none',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: rank === 1 ? 'transparent' : 'white',
                    }}
                  >
                    #{rank}
                  </Typography>
                  <Stack
                    sx={{
                      gap: 0.5,
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      component="p"
                      variant={isMobile ? 'subtitle3' : 'h5'}
                      sx={{ color: 'neutral.100' }}
                    >
                      {formatAddress(address, 4, 2)}
                    </Typography>
                    {isMyEntry && <MyEntryLabel />}
                  </Stack>
                </Box>
                <Stack
                  sx={{
                    alignItems: 'center',
                    width: '100%',
                    py: { xs: 1, md: 1.5 },
                    px: { xs: 1, md: 1.5 },
                    gap: { xs: 0.5, md: 1.5 },
                    bgcolor: 'background.paper',
                    borderRadius: '8px',
                    border: { xs: 'none', md: '1px solid' },
                    borderColor: { xs: 'unset', md: 'border.strong' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: 10, md: 12 },
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      {`${getTargetLabel(campaign.type)}`}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: 12, md: 20 },
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      <CompactNumberWithTooltip value={result} />
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: 10, md: 12 },
                        fontWeight: 500,
                        lineHeight: 1,
                        mr: { xs: 0.5, md: 0 },
                      }}
                    >
                      Score
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: 12, md: 20 },
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      <CompactNumberWithTooltip value={score} />
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: 10, md: 12 },
                        fontWeight: 500,
                        lineHeight: 1,
                        mr: { xs: 0.5, md: 0 },
                      }}
                    >
                      Reward
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: 12, md: 20 },
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      <FormattedNumber
                        value={rewardValue}
                        decimals={rewardDecimals}
                        prefix="$"
                        suffix={rewardSuffix}
                      />
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            );
          })}
        </Box>
        <Stack sx={{ position: 'relative' }}>
          {showList && (
            <LeaderboardList
              data={leaderboard.data.slice(listStart, listEnd)}
              activeAddress={activeAddress}
              campaignType={campaign.type}
            />
          )}
          {showViewAllButton && (
            <ViewAllButton onClick={() => setIsOverlayOpen(true)} />
          )}
        </Stack>
      </Paper>
      <LeaderboardOverlay
        open={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
        data={leaderboard.data}
        updatedAt={leaderboard.updated_at}
        symbol={campaign.symbol}
        campaignType={campaign.type}
      />
    </Stack>
  );
};

export default Leaderboard;
